import { fetchAuthSession } from '@aws-amplify/auth';

import env from '../config/env';
import { BACKEND_AGENTS_API } from '../env';

const API_BASE = BACKEND_AGENTS_API;

// No usar x-api-key para el API de agentes, solo autenticación Cognito
const defaultHeaders = {
  Accept: 'application/json'
};

/**
 * Translate a known backend validation message into a clear, actionable
 * one for the end user. Falls back to the original technical message for
 * anything not recognized, so unexpected errors are never silently masked.
 */
function friendlyAgentErrorMessage(technicalMessage) {
  if (!technicalMessage) {
    return 'No se pudo completar la operación con el servicio de agentes.';
  }
  if (/no posee tablas configuradas/i.test(technicalMessage)) {
    return 'El modelo semántico seleccionado no tiene ninguna tabla asignada. Ve a "Semantic Models", ábrelo y agrega al menos una tabla antes de crear el agente.';
  }
  if (/semantic workspace no encontrado/i.test(technicalMessage)) {
    return 'No encontramos el espacio de análisis seleccionado. Actualiza la página y vuelve a intentarlo.';
  }
  return technicalMessage;
}

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();

    // 🔍 DEBUG: Log token information
    console.log('🔐 AgentService.getAuthHeader - Token Debug:');
    console.log('  Tokens:', tokens ? 'Present' : 'Missing');
    console.log('  ID Token:', idToken ? `Present (${idToken.substring(0, 50)}...)` : 'Missing');
    
    // Decode and log token payload (without verification)
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        console.log('  Token Payload:', {
          sub: payload.sub,
          email: payload.email,
          iss: payload.iss,
          aud: payload.aud,
          token_use: payload.token_use,
          exp: new Date(payload.exp * 1000).toISOString(),
          expired: payload.exp * 1000 < Date.now()
        });
      } catch (e) {
        console.error('  Error decoding token:', e);
      }
      
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch (error) {
    console.error('❌ AgentService.getAuthHeader: unable to fetch session', error);
  }

  console.warn('⚠️  AgentService.getAuthHeader: No token available');
  return {};
}

async function request(path, init = {}) {
  const authHeader = await getAuthHeader();

  const headers = {
    ...defaultHeaders,
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  // 🔍 DEBUG: Log request details
  console.log('📤 AgentService.request - Outgoing Request:');
  console.log('  URL:', url);
  console.log('  Method:', init.method || 'GET');
  console.log('  Headers:', headers);
  console.log('  Has Authorization:', !!headers.Authorization);
  if (headers.Authorization) {
    console.log('  Auth Header Format:', headers.Authorization.substring(0, 20) + '...');
  }

  let response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      mode: 'cors',
      cache: 'no-cache'
    });
  } catch (networkError) {
    console.error('❌ Network Error:', networkError);
    throw new Error(`No se pudo contactar el servicio de agentes (${url}). ${networkError.message}`);
  }

  const requestId =
    response.headers.get('x-amzn-requestid') ||
    response.headers.get('x-amz-apigw-id') ||
    response.headers.get('x-request-id') ||
    'n/a';

  // 🔍 DEBUG: Log response details
  console.log('📥 AgentService.request - Response:');
  console.log('  Status:', response.status, response.statusText);
  console.log('  Request ID:', requestId);
  console.log('  Headers:', Object.fromEntries(response.headers.entries()));

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    console.error('❌ Error Response Body:', errorBody);

    let technicalMessage = errorBody || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(errorBody);
      technicalMessage = parsed.message || parsed.error || technicalMessage;
    } catch {
      // Response body wasn't JSON — keep the raw text as the technical message.
    }

    const error = new Error(friendlyAgentErrorMessage(technicalMessage));
    error.technicalMessage = `HTTP ${response.status} (reqId=${requestId}): ${technicalMessage}`;
    error.status = response.status;
    error.requestId = requestId;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    console.log('✅ Response Data:', data);
    return data;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    const data = JSON.parse(text);
    console.log('✅ Response Data (parsed):', data);
    return data;
  } catch {
    console.log('✅ Response Text:', text);
    return text;
  }
}

function ensureArray(value) {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

// Tools que agents-admin sabe derivar/validar (agent_derivation.py / VALID_TOOLS
// en agents-admin/app.py) -- mantener en sync si se agregan conectores nuevos.
const CONNECTOR_TOOLS = ['snowflake', 'mysql', 'postgresql', 'sqlserver', 'oracle', 'mongodb'];

function normalizeAgentRecord(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const connectionIds = CONNECTOR_TOOLS.reduce((acc, tool) => {
    const value = item[`${tool}_connection_id`];
    if (value) {
      acc[tool] = value;
    }
    return acc;
  }, {});

  return {
    agentId: item.config_id || null,
    agentName: item.agent_name || null,
    agentStatus: item.status || null,
    semanticWorkspaceId: item.semantic_workspace_id || null,
    semanticModelId: item.semantic_model_id || null,
    semanticModelName: item.semantic_model_name || null,
    accountId: item.account_id || null,
    foundationModel: item.foundation_model || null,
    instruction: item.instruction || null,
    enabledTools: ensureArray(item.enabled_tools),
    connectionIds,
    // El backend ya NO expone el id de Bedrock -- el agente se liga por
    // `knowledge_base_ref` (id whisperbi de la pantalla "Bases de conocimiento").
    knowledgeBaseRef: item.knowledge_base_ref || null,
    knowledgeBaseName: item.knowledge_base_name || null,
    knowledgeBases: item.knowledge_base_ref
      ? [{ knowledgeBaseId: item.knowledge_base_ref, name: item.knowledge_base_name || null }]
      : [],
    createdAt: item.created_at || null,
    raw: item
  };
}

function buildCreatePayload(source = {}) {
  if (!source || typeof source !== 'object') {
    throw new Error('El payload para crear el agente es inválido.');
  }

  const workspaceId = source.semantic_workspace_id || source.semanticWorkspaceId;
  if (!workspaceId) {
    throw new Error('Falta el campo requerido: semantic_workspace_id');
  }

  // account_id/user_id NO se envían: agents-admin deriva account_id del JWT
  // ya verificado por API Gateway -- mandarlo desde el cliente es incorrecto
  // respecto a su modelo de seguridad actual (ver _extract_account_id).
  const payload = { semantic_workspace_id: String(workspaceId) };

  const companyName = source.company_name || source.companyName;
  if (companyName) {
    payload.company_name = String(companyName);
  }

  const semanticModelId = source.semantic_model_id || source.semanticModelId;
  if (semanticModelId) {
    payload.semantic_model_id = String(semanticModelId);
  }

  return payload;
}

const AgentService = {
  async createAgent(payload) {
    const body = buildCreatePayload(payload);
    const response = await request('/agents', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    return { agent: normalizeAgentRecord(response), raw: response };
  },

  async updateAgent(configId, fields) {
    if (!configId) {
      throw new Error('AgentService.updateAgent: configId es requerido');
    }

    const response = await request('/agents', {
      method: 'PUT',
      body: JSON.stringify({ config_id: configId, ...fields })
    });

    return { agent: normalizeAgentRecord(response), raw: response };
  },

  // Devuelve UNA página: { agents, nextCursor, raw }. `nextCursor` es null
  // cuando ya no quedan páginas. `maxResults` se mantiene como alias de
  // `limit` (es el nombre que usaba el backend antes); el backend acota a
  // 100, así que pedir 200 devuelve 100 y un cursor.
  async listAgents({ workspaceId, limit, cursor, maxResults } = {}) {
    const params = new URLSearchParams();

    if (workspaceId) {
      params.set('workspace_id', workspaceId);
    }
    const pageSize = limit ?? maxResults;
    if (pageSize !== undefined && pageSize !== null) {
      params.set('limit', String(pageSize));
    }
    if (cursor) {
      params.set('cursor', cursor);
    }

    const query = params.toString();
    const response = await request(query ? `/agents?${query}` : '/agents', {
      method: 'GET'
    });

    const agents = ensureArray(response?.agents).map(normalizeAgentRecord).filter(Boolean);

    return { agents, nextCursor: response?.next_cursor || null, raw: response };
  },

  // Recorre TODAS las páginas y devuelve el conjunto completo.
  //
  // Para quien necesita contar o cruzar contra otro conjunto, no para
  // mostrar un listado. El tablero cuenta "agentes activos" y "modelos sin
  // agente": con una sola página, el primero queda capado en silencio y el
  // segundo queda directamente MAL (sobrecuenta los modelos sin agente,
  // porque los agentes que faltan parecen no existir). Un selector de
  // agentes de un workspace tiene el mismo problema: un agente que no
  // entró en la página simplemente no se puede elegir.
  //
  // El tope de vueltas evita que un bug de cursor deje esto girando: es
  // deliberadamente generoso (100 x 100 = 10.000 agentes) y si se alcanza
  // se avisa en consola en vez de devolver un conjunto incompleto en
  // silencio.
  async listAllAgents({ workspaceId } = {}) {
    const agents = [];
    let cursor = null;
    let vueltas = 0;

    do {
      // eslint-disable-next-line no-await-in-loop
      const page = await AgentService.listAgents({ workspaceId, limit: 100, cursor });
      agents.push(...page.agents);
      cursor = page.nextCursor;
      vueltas += 1;
      if (vueltas >= 100) {
        console.warn('AgentService.listAllAgents: se alcanzó el tope de páginas; el resultado puede estar incompleto.');
        break;
      }
    } while (cursor);

    return { agents };
  },

  async deleteAgent(agentId) {
    if (!agentId) {
      throw new Error('AgentService.deleteAgent: agentId es requerido');
    }

    return request(`/agents/${encodeURIComponent(agentId)}`, {
      method: 'DELETE'
    });
  },

  async getAgentDetails(agentId) {
    if (!agentId) {
      throw new Error('AgentService.getAgentDetails: agentId es requerido');
    }

    const response = await request(`/agents/${encodeURIComponent(agentId)}`, { method: 'GET' });

    return { agent: normalizeAgentRecord(response), raw: response };
  }
  // La KB ligada a un agente se consulta con KnowledgeBaseService.getKnowledgeBase(agent.knowledgeBaseRef)
  // -- ya no hay un endpoint /agents/{id}/knowledge-base (devolvía ARNs de AWS).
};

export default AgentService;
